/**
 * Analytics Event Catalog
 * Centralized event names for type-safe tracking.
 *
 * For GA-bound events, the catalog **value** is the GA4 wire name (snake_case).
 * Enum keys may still diverge from wording on purpose — do not "fix" these:
 *   REACTION_ADDED    → create_reactions_event
 *   REACTION_REMOVED  → delete_reactions_event
 *   SEARCH_PERFORMED  → gokollab_search_event
 *   LESSON_STARTED    → lesson_opened_event
 *   LOGIN_SUCCESS     → custom_login_event
 *   COURSE_VIEWED     → community_course_opened_event
 */

export const AnalyticsEvents = {
  // Page Views
  PAGE_VIEW: 'page_view',

  // Authentication
  LOGIN_SUCCESS: 'custom_login_event',
  LOGIN_FAILED: 'login_failed',
  LOGOUT: 'logout_event',
  SIGNUP_COMPLETED: 'sign_up',
  SIGNUP_FAILED: 'signup_failed',

  // Backend System Events (Elasticsearch) — catalog stays semantic; wire map is in event-policy
  USER_AUTO_LOGOUT: 'user_auto_logout',
  COMMUNITIES_USER_AUTO_LOGOUT: 'communities_user_auto_logout',
  GENERATE_TOKEN_FAILED: 'generate_token_failed',
  GENERATE_TOKEN_SUCCESS: 'generate_token_success',
  COMMUNITIES_GROUP_ACCESS: 'communities_group_access',
  NO_COMMUNITIES_FOUND: 'no_communities_found',
  COURSE_ACCESS_REFRESH: 'course_access_refresh',
  PUSH_NOTIFICATION_INITIALIZED: 'push_notification_initialized',
  FORCE_UPDATE_EVENT: 'force_update_event',

  // Posts
  POST_CREATED: 'post_created_event',
  POST_EDITED: 'post_edited_event',
  POST_DELETED: 'post_deleted_event',
  POST_VIEWED: 'post_viewed',
  POST_SHARED: 'post_shared',

  // Comments
  COMMENT_CREATED: 'comment_created_event',
  COMMENT_EDITED: 'comment_edited_event',
  COMMENT_DELETED: 'comment_deleted_event',

  // Reactions (wire wording preserved)
  REACTION_ADDED: 'create_reactions_event',
  REACTION_REMOVED: 'delete_reactions_event',

  // Groups
  GROUP_CREATED: 'group_created',
  GROUP_JOINED: 'group_joined_event',
  GROUP_LEFT: 'group_left',
  GROUP_VIEWED: 'group_viewed',

  // Channels
  CHANNEL_CREATED: 'channel_created',
  CHANNEL_VIEWED: 'channel_viewed',
  CHANNEL_JOINED: 'channel_joined',

  // Courses
  COURSE_VIEWED: 'community_course_opened_event',
  COURSE_ENROLLED: 'course_enrolled',
  COURSE_ENROLLMENT_STARTED: 'course_enrollment_started',
  COURSE_COMPLETED: 'course_completed',
  COURSE_DISCOVERED: 'course_discovered_event',
  NEXT_MODULE: 'next_module',
  VIEWS_ALL_SESSIONS: 'views_all_sessions',
  LESSON_STARTED: 'lesson_opened_event',
  LESSON_COMPLETED: 'lesson_completed_event',
  ANNOUNCEMENTS_OPENED: 'announcements_opened',
  ANNOUNCEMENT_VIEWED: 'announcement_viewed',
  ANNOUNCEMENT_COURSE_CLICKED: 'announcement_course_clicked',

  // Events
  EVENT_CREATED: 'event_created_event',
  EVENT_VIEWED: 'event_viewed',
  EVENT_RSVP_YES: 'event_rsvp_event',
  EVENT_RSVP_NO: 'event_rsvp_event',

  // Search
  SEARCH_PERFORMED: 'gokollab_search_event',
  SEARCH_RESULT_CLICKED: 'gokollab_search_event',

  // Chat
  CHAT_MESSAGE_SENT: 'chat_message_sent_event',
  CHAT_MESSAGE_FAILED: 'chat_message_failed_event',
  CHAT_BLOCKED: 'chat_blocked_event',
  CHAT_CREATED: 'chat_created_event',

  // GoKollab discovery
  GOKOLLAB_HOMEPAGE_LAND: 'gokollab_homepage_land_event',
  GOKOLLAB_LISTING_VIEW: 'gokollab_listing_view_event',
  GOKOLLAB_LISTING_JOINED: 'gokollab_listing_joined_event',
  GOKOLLAB_COMMUNITIES_VIEW: 'gokollab_communities_view_event',
  GOKOLLAB_COURSES_VIEW: 'gokollab_courses_view_event',
  GOKOLLAB_CREATORS_VIEW: 'gokollab_creators_view_event',
  GOKOLLAB_CATEGORY_SWITCH: 'gokollab_category_switch_event',
  GOKOLLAB_COURSE_CREATE: 'gokollab_course_create_event',
  GOKOLLAB_COMMUNITY_CREATE: 'gokollab_community_create_event',

  // Kollab Pro funnel
  GOKOLLAB_PROMO_MODAL_VIEW: 'gokollab_promo_modal_view',
  GOKOLLAB_PROMO_MODAL_CLOSE: 'gokollab_promo_modal_close',
  GOKOLLAB_PROMO_MODAL_CTA: 'gokollab_promo_modal_cta',
  GOKOLLAB_PRICING_PAGE_VIEW: 'gokollab_pricing_page_view',
  GOKOLLAB_PRICING_START_TRIAL: 'gokollab_pricing_start_trial',
  GOKOLLAB_TRIAL_SIGNUP_VIEW: 'gokollab_trial_signup_view',
  GOKOLLAB_TRIAL_SIGNUP_SUBMIT: 'gokollab_trial_signup_submit',
  GOKOLLAB_TRIAL_SIGNUP_SUCCESS: 'gokollab_trial_signup_success',
  GOKOLLAB_TRIAL_SIGNUP_FAIL: 'gokollab_trial_signup_fail',

  // Courses navigation
  LESSON_OPENED: 'lesson_opened_event',
  NEXT_LESSON: 'next_lesson_event',
  COMMUNITY_COURSE_OPENED: 'community_course_opened_event',

  // Social / mentions
  FOLLOW: 'follow_event',
  UNFOLLOW: 'unfollow_event',
  COURSE_MENTIONED: 'course_mentioned_event',
  EVENT_MENTIONED: 'event_mentioned_event',
  CHANNEL_MENTIONED: 'channel_mentioned_event',
  GO_LIVE_SESSION_JOINED: 'go_live_session_joined_event',

  // Moderation / onboarding
  USER_BANNED: 'user_banned_event',
  MEDIA_REMOVED: 'media_removed_event',
  EVENT_ATTENDEES_VIEWED: 'event_attendees_viewed_event',
  EVENT_ATTENDEES_LIST_EXPORTED: 'event_attendees_list_exported_event',
  MEMBER_VIEWED_OWN_ANSWERS: 'member_viewed_own_answers_event',
  ADMIN_VIEWED_MEMBER_ANSWERS: 'admin_viewed_member_answers_event',

  // Certificates
  CERTIFICATE_DOWNLOAD: 'certificate_download_event',

  // RSVP aggregate (same wire as yes/no)
  EVENT_RSVP: 'event_rsvp_event',

  // Profile
  PROFILE_VIEWED: 'profile_viewed',
  PROFILE_EDITED: 'profile_edited',

  // Media
  MEDIA_UPLOADED: 'media_uploaded_event',
  VIDEO_PLAYED: 'video_played',
  VIDEO_COMPLETED: 'video_completed',

  // Errors
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
  /** Dedicated backend-sink event for a failed analytics context resolution (controlled payload
   * only — never reuse the generic ERROR_OCCURRED, whose raw window errors must not reach the ES sink). */
  ANALYTICS_CONTEXT_RESOLVER_ERROR: 'analytics_context_resolver_error',

  // UI Interactions
  ELEMENT_CLICKED: 'element_clicked',
  NAVIGATION_CLICKED: 'navigation_clicked',

  // Forms
  FORM_STARTED: 'form_started',
  FORM_COMPLETED: 'form_completed',
  FORM_ABANDONED: 'form_abandoned',

  // Community creator analytics (group settings)
  CA_VIEWED: 'ca_viewed',
  CA_DATE_RANGE_CHANGED: 'ca_date_range_changed',
  CA_INACTIVE_EXPORTED: 'ca_inactive_exported',
  CA_RETRY_CLICKED: 'ca_retry_clicked',
  CA_LOAD_FAILED: 'ca_load_failed',
} as const

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents]
